import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useSeason } from './SeasonContext';
import {
  getPlayers,
  getGames,
  createGame,
  updatePlayer,
  getPlayer,
  getScheduledGames,
  createScheduledGame,
  updateScheduledGame,
  deleteScheduledGame,
  deleteGame,
  createSeasonGame,
  getSeasonGames,
  deleteSeasonGame,
  getSeasonStats,
  updateSeasonGame,
  recalculateCareerStats
} from '../firebase/firestore';
const PlayerContext = createContext();

// Default empty player data structure
const emptyPlayerData = {
  name: "",
  number: "",
  position: "",
  leagues: [],
  recentGames: [],
  scheduledGames: [],
  seasons: {
    "2024": {
      battingStats: {
        average: 0,
        obp: 0,
        slg: 0,
        ops: 0,
        atBats: 0,
        hits: 0,
        doubles: 0,
        triples: 0,
        homeRuns: 0,
        runs: 0,
        rbi: 0,
        totalBases: 0,
        walks: 0,
        strikeouts: 0,
        hitByPitch: 0,
        games: 0
      },
      pitchingStats: {
        era: 0,
        whip: 0,
        wins: 0,
        losses: 0,
        inningsPitched: 0,
        strikeouts: 0,
        walks: 0,
        hits: 0,
        earnedRuns: 0
      }
    }
  }
};
// Provider component
export const PlayerProvider = ({ children }) => {
  // Get user from auth context
  const { currentUser } = useAuth();
  // Get season context
  const { activeSeason } = useSeason();
  
  // State
  const [playerData, setPlayerData] = useState(emptyPlayerData);
  const [allPlayers, setAllPlayers] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showQuickEntry, setShowQuickEntry] = useState(false);
  const [viewingAllSeasons, setViewingAllSeasons] = useState(false);
  const [dataVersion, setDataVersion] = useState(0); // Increment when data changes to signal cache invalidation
  // Fetch all players for the current user
  // Fetch player data for the selected player
  const fetchPlayerData = useCallback(async (playerId = selectedPlayerId) => {
    if (!currentUser || !playerId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch basic player data, scheduled games, and all games in parallel
      const [player, scheduledGames, allGames] = await Promise.all([
        getPlayer(currentUser.uid, playerId),
        getScheduledGames(currentUser.uid, playerId),
        getGames(currentUser.uid, playerId)
      ]);
      
      const fullPlayerData = {
        ...player,
        recentGames: allGames || [],
        scheduledGames: scheduledGames || [],
        seasonGames: []
      };

      // If there's an active season and we're not viewing all seasons, get season-specific data
      if (activeSeason && !viewingAllSeasons) {
        try {
          // Fetch season-specific data in parallel
          const [seasonGames, seasonStats] = await Promise.all([
            getSeasonGames(currentUser.uid, playerId, activeSeason.id),
            getSeasonStats(currentUser.uid, playerId, activeSeason.id).catch(() => null)
          ]);
          
          fullPlayerData.seasonGames = seasonGames || [];
          if (seasonStats) {
            fullPlayerData.seasonStats = seasonStats;
          }
        } catch (err) {
          console.error('Error fetching season data:', err);
          // Don't fail the whole fetch if season data can't be loaded
        }
      }

      setPlayerData(fullPlayerData);
      // Increment data version to signal cache invalidation to useStats
      setDataVersion(prev => prev + 1);
    } catch (err) {
      console.error('Error fetching player data:', err);
      setError('Failed to load player data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, activeSeason, viewingAllSeasons, selectedPlayerId]);
  const fetchAllPlayers = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const players = await getPlayers(currentUser.uid);
      setAllPlayers(players);
      
      // Select the first player if none is selected and players exist
      if (players.length > 0 && !selectedPlayerId) {
        setSelectedPlayerId(players[0].id);
      }
    } catch (err) {
      console.error('Error fetching players:', err);
      setError('Failed to load players. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, selectedPlayerId]);
  // Handle player selection
  const selectPlayer = useCallback((playerId) => {
    setSelectedPlayerId(playerId);
  }, []);

  // Toggle between viewing all seasons or just the active season
  const toggleViewAllSeasons = useCallback((viewAll) => {
    setViewingAllSeasons(viewAll);
    if (selectedPlayerId) {
      fetchPlayerData(selectedPlayerId);
    }
  }, [selectedPlayerId, fetchPlayerData]);

  // Function to add a new game
  const addGame = useCallback(async (gameData) => {
    if (!currentUser || !selectedPlayerId) {
      setError('No player selected');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      if (activeSeason && !viewingAllSeasons) {
        // Create a game associated with the active season
        await createSeasonGame(currentUser.uid, selectedPlayerId, activeSeason.id, gameData);
      } else {
        // Create a game without season association
        await createGame(currentUser.uid, selectedPlayerId, gameData);
      }
      
      // Refresh player data to include the new game
      await fetchPlayerData();
      
    } catch (err) {
      console.error('Error adding game:', err);
      const errorMessage = err.message || 'Failed to add game. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage); // Throw so caller can handle it
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, selectedPlayerId, activeSeason, viewingAllSeasons, fetchPlayerData]);
  // Function to delete an existing game
  const removeGame = useCallback(async (gameId, seasonId = null) => {
    if (!currentUser || !selectedPlayerId) {
      setError('No player selected');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      if (seasonId) {
        // Delete a season-specific game
        await deleteSeasonGame(currentUser.uid, selectedPlayerId, gameId);
      } else {
        // Delete a regular game
        await deleteGame(currentUser.uid, selectedPlayerId, gameId);
      }
      
      // Refresh player data to update stats and game list
      await fetchPlayerData();
      
    } catch (err) {
      console.error('Error deleting game:', err);
      const errorMessage = err.message || 'Failed to delete game. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage); // Throw so caller can handle it
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, selectedPlayerId, fetchPlayerData]);

  // Function to update an existing game
  const updateGame = useCallback(async (gameId, gameData) => {
    if (!currentUser || !selectedPlayerId) {
      setError('No player selected');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Update the game in Firestore
      await updateSeasonGame(currentUser.uid, selectedPlayerId, gameId, gameData);
      
      // Refresh player data to update stats and game list
      await fetchPlayerData();
      
      return true;
    } catch (err) {
      console.error('Error updating game:', err);
      const errorMessage = err.message || 'Failed to update game. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage); // Throw so caller can handle it
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, selectedPlayerId, fetchPlayerData]);

  // Function to update player profile
  const updateProfile = useCallback(async (profileData) => {
    if (!currentUser || !selectedPlayerId) {
      setError('No player selected');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Update the player in Firestore
      await updatePlayer(currentUser.uid, selectedPlayerId, profileData);
      
      // Refresh player data
      await fetchPlayerData();
      
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, selectedPlayerId, fetchPlayerData]);
  // Reset player state (useful for logout)
  const resetPlayerState = useCallback(() => {
    setPlayerData(emptyPlayerData);
    setIsLoading(false);
    setError(null);
    setSelectedPlayerId(null);
    setAllPlayers([]);
    setShowQuickEntry(false);
    setViewingAllSeasons(false);
  }, []);
  // Effect to fetch players when the user changes
  useEffect(() => {
    if (currentUser) {
      fetchAllPlayers();
    } else {
      resetPlayerState();
    }
  }, [currentUser, fetchAllPlayers, resetPlayerState]);
  // Effect to fetch player data when the selected player or active season changes
  // Note: We depend on the actual values that should trigger a fetch, not on fetchPlayerData itself
  // This prevents redundant fetches when fetchPlayerData is recreated due to other dependencies
  useEffect(() => {
    if (selectedPlayerId) {
      fetchPlayerData(selectedPlayerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlayerId, activeSeason?.id, viewingAllSeasons]);
  // Scheduled Games Management Functions
  
  // Fetch scheduled games for the selected player
  const fetchScheduledGames = useCallback(async (playerId = selectedPlayerId) => {
    if (!currentUser || !playerId) {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Get scheduled games for the player
      const games = await getScheduledGames(currentUser.uid, playerId);
      
      // Update player data with scheduled games
      setPlayerData(prevData => ({
        ...prevData,
        scheduledGames: games || []
      }));
      
    } catch (err) {
      console.error('Error fetching scheduled games:', err);
      setError('Failed to load scheduled games. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, selectedPlayerId, setPlayerData]);
  const addScheduledGame = useCallback(async (gameData) => {
    if (!currentUser || !selectedPlayerId) {
      setError('No player selected');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Create the scheduled game in Firestore
      await createScheduledGame(currentUser.uid, selectedPlayerId, gameData);
      
      // Refresh scheduled games
      await fetchScheduledGames();
      
    } catch (err) {
      console.error('Error adding scheduled game:', err);
      setError('Failed to add scheduled game. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, selectedPlayerId, fetchScheduledGames]);
  
  // Delete a scheduled game
  const removeScheduledGame = useCallback(async (gameId) => {
    if (!currentUser || !selectedPlayerId) {
      setError('No player selected');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Delete the scheduled game from Firestore
      await deleteScheduledGame(currentUser.uid, selectedPlayerId, gameId);
      
      // Refresh scheduled games
      await fetchScheduledGames();
      
    } catch (err) {
      console.error('Error deleting scheduled game:', err);
      setError('Failed to delete scheduled game. Please try again.');
    } finally {
      setIsLoading(false);
    }

  }, [currentUser, selectedPlayerId, fetchScheduledGames]);
  
  // Update a scheduled game
  const updateScheduledGameData = useCallback(async (gameId, gameData) => {
    if (!currentUser || !selectedPlayerId) {
      setError('No player selected');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Update the scheduled game in Firestore
      await updateScheduledGame(currentUser.uid, selectedPlayerId, gameId, gameData);

      // Refresh scheduled games
      await fetchScheduledGames();

    } catch (err) {
      console.error('Error updating scheduled game:', err);
      setError('Failed to update scheduled game. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, selectedPlayerId, fetchScheduledGames]);

  // Recalculate career stats from scratch
  const recalculateStats = useCallback(async () => {
    if (!currentUser || !selectedPlayerId) {
      setError('No player selected');
      throw new Error('No player selected');
    }

    try {
      setIsLoading(true);
      setError(null);

      // Recalculate career stats using the corrected formulas
      const updatedStats = await recalculateCareerStats(currentUser.uid, selectedPlayerId);

      // Refresh player data to show the updated stats
      await fetchPlayerData();

      return updatedStats;
    } catch (err) {
      console.error('Error recalculating career stats:', err);
      const errorMessage = err.message || 'Failed to recalculate career stats. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, selectedPlayerId, fetchPlayerData]);
  // Context value
  const value = useMemo(() => ({
    playerData,
    allPlayers,
    selectedPlayerId,
    isLoading,
    error,
    showQuickEntry,
    activeSeason,
    viewingAllSeasons,
    dataVersion,
    setShowQuickEntry,
    addGame,
    removeGame,
    updateGame,
    updateProfile,
    fetchPlayerData,
    fetchAllPlayers,
    selectPlayer,
    toggleViewAllSeasons,
    resetPlayerState,
    // Scheduled games functions
    addScheduledGame,
    removeScheduledGame,
    updateScheduledGameData,
    fetchScheduledGames,
    // Stats recalculation
    recalculateStats
  }), [
    playerData,
    allPlayers,
    selectedPlayerId,
    isLoading,
    error,
    showQuickEntry,
    activeSeason,
    viewingAllSeasons,
    dataVersion,
    // Functions
    addGame,
    removeGame,
    updateGame,
    updateProfile,
    fetchPlayerData,
    fetchAllPlayers,
    selectPlayer,
    toggleViewAllSeasons,
    resetPlayerState,
    addScheduledGame,
    removeScheduledGame,
    updateScheduledGameData,
    fetchScheduledGames,
    recalculateStats
  ]);

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
};
// Custom hook to use the player context
export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};
